import axios from 'axios';
import config from '../config/app';

// Use environment-configured API base URL
const API_BASE_URL = config.apiBasePath;

console.log('API_BASE_URL:', API_BASE_URL);

// Create axios instance with interceptors for authentication
const apiClient = axios.create({
  baseURL: API_BASE_URL
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('sessionToken');
  console.log('Session token from localStorage:', token ? `${token.substring(0, 20)}...` : 'null');
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    console.log('Added Authorization header to request');
  } else {
    console.warn('No session token found in localStorage');
  }
  
  console.log('Request config:', {
    url: config.url,
    method: config.method,
    hasAuthHeader: !!config.headers.Authorization
  });
  
  return config;
});

class UploadService {
  async getSignedUrl(fileName, fileType) {
    try {
      const response = await apiClient.post('/upload/signed-url', {
        fileName,
        fileType
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get signed URL:', error);
      throw new Error(error.response?.data?.error || 'Failed to get upload URL');
    }
  }

  async uploadFile(file, onProgress) {
    try {
      console.log(`📁 Starting upload for: ${file.name} (${this.formatFileSize(file.size)})`);
      
      // Step 1: Get signed URL from backend (via Node.js proxy)
      const { signedUrl, fileName } = await this.getSignedUrl(file.name, file.type);
      console.log(`🔗 Got signed URL for: ${fileName}`);

      // Check if we should use proxy upload (when signed URLs don't work)
      if (signedUrl.includes('/upload/proxy')) {
        return this.uploadViaProxy(file, onProgress);
      }

      // Step 2: Upload directly to GCS using signed URL
      const uploadResponse = await axios.put(signedUrl, file, {
        headers: {
          'Content-Type': file.type,
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          if (onProgress) {
            onProgress(percentCompleted);
          }
        },
      });

      if (uploadResponse.status === 200) {
        console.log(`✅ Real upload complete: ${fileName}`);
        return {
          success: true,
          fileName,
          message: 'File uploaded successfully to GCS'
        };
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      
      // If direct upload fails, try proxy upload as fallback
      if (error.response?.status === 403 || error.message.includes('CORS')) {
        console.log('Direct upload failed, trying proxy upload...');
        return this.uploadViaProxy(file, onProgress);
      }
      
      // Provide user-friendly error messages
      if (error.response?.status === 401) {
        throw new Error('Authentication required. Please refresh and try again.');
      } else if (error.response?.status === 403) {
        throw new Error('You do not have permission to upload files.');
      } else if (error.response?.status === 413) {
        throw new Error('File too large. Maximum size is 10MB.');
      } else if (error.response?.status >= 500) {
        throw new Error('Server error. Please try again later.');
      } else {
        throw new Error(error.response?.data?.message || 'Upload failed. Please try again.');
      }
    }
  }

  async uploadViaProxy(file, onProgress) {
    try {
      console.log(`🔀 Using proxy upload for: ${file.name}`);
      
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post('/upload/proxy', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          if (onProgress) {
            onProgress(percentCompleted);
          }
        },
      });

      console.log(`✅ Proxy upload complete: ${response.data.fileName}`);
      return {
        success: true,
        fileName: response.data.fileName,
        message: 'File uploaded successfully via backend'
      };
    } catch (error) {
      console.error('Proxy upload error:', error);
      throw new Error(error.response?.data?.detail || 'Proxy upload failed');
    }
  }

  // Simulate upload for UI testing
  async simulateUpload(file, onProgress) {
    console.log(`🎭 Simulating upload for: ${file.name} (${this.formatFileSize(file.size)})`);
    
    return new Promise((resolve) => {
      let progress = 0;
      const interval = setInterval(() => {
        progress += Math.random() * 15 + 5; // Random progress between 5-20%
        progress = Math.min(progress, 100);
        
        if (onProgress) {
          onProgress(Math.round(progress));
        }
        
        if (progress >= 100) {
          clearInterval(interval);
          const fileName = `simulated_${Date.now()}_${file.name}`;
          console.log(`✅ Simulation complete: ${fileName}`);
          resolve({
            success: true,
            fileName,
            message: 'File uploaded successfully (simulated)'
          });
        }
      }, 200); // Update every 200ms for smooth progress
    });
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}

export const uploadService = new UploadService();
